import React, { useEffect, useState } from "react";
import { Typography, IconButton, Button, Tooltip } from "@material-tailwind/react";
import { Popover } from "react-tiny-popover";
import {
    ArrowDownTrayIcon,
    ArrowUpTrayIcon,
    EllipsisVerticalIcon,
    FunnelIcon,
    InformationCircleIcon,
} from "@heroicons/react/24/solid";
import { useDispatch, useSelector } from "react-redux";
import { approveRejectRefundRequest, downloadRefundRequest, fetchRefundRequest, uploadRefundPaymentSlip } from "@/store/slices/orderSlice";
import Swal from "sweetalert2";
import Toaster from "@/components/Toaster";
import { handleDownloadURL } from "@/components/DownloadFile";
import RefundApprovalModal from "./order-modals/refund-approvel-modal";
import { UploadRefundSlipModal } from "./order-modals/upload-refund-slip-modal";
import { useTranslation } from "react-i18next";


const STATUS_STYLES = {
    Pending: "bg-amber-100 text-amber-700",
    pending: "bg-amber-100 text-amber-700",
    Approved: "bg-green-100 text-green-700",
    completed: "bg-green-100 text-green-700",
    approved: "bg-green-100 text-green-700",
    Rejected: "bg-red-100 text-red-700",
    rejected: "bg-red-100 text-red-700",
};

function formatDate(dateStr) {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
    }) + " | " + d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: true });
}

export function OrderRefundRequests({ orderId }) {
    const { t } = useTranslation('order');
    const { t: ct } = useTranslation('common');

    const dispatch = useDispatch();
    const [filter, setFilter] = useState('');
    const { user } = useSelector((state) => state.user);
    const { rfq_summary } = useSelector((state) => state.rfq);
    const { refund_requests, loading, order_details } = useSelector((state) => state.order);

    const [checkedIds, setCheckedIds] = useState([]);
    const [actionPopoverOpen, setActionPopoverOpen] = useState({});
    const [isFilterPopoverOpen, setIsFilterPopoverOpen] = useState(false);

    const [isRefondModalOpen, setIsRefondModalOpen] = useState(false);
    const { order_payments } = useSelector((state) => state.order);
    const isPaymentApproved = order_payments?.payments?.some(
        (p) => p.status === "approved"
    );

    const list = Array.isArray(refund_requests)
        ? refund_requests
        : refund_requests && typeof refund_requests === "object"
            ? [refund_requests]
            : [];

    const currentRefundRequest = list.find(item => item.id === isRefondModalOpen);

    const targetUserId =
        currentRefundRequest?.order?.user_id ||
        order_details?.customer?.id ||
        rfq_summary?.user_id;

    useEffect(() => {
        if (orderId) dispatch(fetchRefundRequest({ order_id: orderId, filter: filter }));
    }, [orderId, dispatch, filter]);

    const toggleActionPopover = (id) => {
        setActionPopoverOpen((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    const handleDownload = (item) => {
        dispatch(downloadRefundRequest({ id: item?.id, }))
            .unwrap()
            .then((res) => {
                handleDownloadURL({ fileUrl: res?.data?.pdf_url });
            })
            .catch(() =>
                Toaster.error(t("download_failed"))
            );
    };

    const onApproveReject = (type, id) => {
        setActionPopoverOpen((prev) => ({ ...prev, [id]: false }));

        if (type === "rejected") {
            Swal.fire({
                title: t("reject_refund_request_title"),
                icon: "warning",
                html: `<p class="text-sm text-gray-600 mb-2">${t('enter_reason_confirmation')}</p>`,
                input: "text",
                inputLabel: t('reason_label'),
                inputPlaceholder: t('enter_reason_placeholder'),
                inputAttributes: { minlength: 2, maxlength: 255 },
                showCancelButton: true,
                confirmButtonText: t('reject_button'),
                confirmButtonColor: "#dc2626",
                preConfirm: (value) => {
                    if (!value || value.trim().length < 2) {
                        Swal.showValidationMessage(t('reason_min_length_2'));
                        return false;
                    }
                    return value.trim();
                },
            }).then((result) => {
                if (result.isConfirmed) {
                    dispatch(approveRejectRefundRequest({
                        status: type,
                        refund_request_id: id,
                        remark: result.value,
                    })).unwrap().then(() => {
                        Toaster.success(t('refund_request_rejected_success'));
                        dispatch(fetchRefundRequest({ order_id: orderId, filter: filter }));
                    }).catch(() => Toaster.error(t('failed_to_perform_action')));
                }
            });
        } else {
            if (!isPaymentApproved) {
                Swal.fire({
                    icon: "error",
                    title: t('payment_not_approved_title'),
                    text: t('payment_pending_approval_text'),
                    confirmButtonColor: "#dc2626",
                });
                return;
            }

            setIsRefondModalOpen(id);
        }
    };

    const approveRefundRequest = (data = {}) => {
        let payload = {
            status: 'approved',
            refund_request_id: isRefondModalOpen,
            ...data
        }
        dispatch(approveRejectRefundRequest(payload))
            .unwrap()
            .then(() => {
                Toaster.success(t('refund_request_approved_success'));
                setIsRefondModalOpen(false);
                dispatch(fetchRefundRequest({ order_id: orderId, filter: filter }));
            })
            .catch(() => Toaster.error(t('failed_to_perform_action')));
    };


    return (
        <div className="w-full">
            <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
                <div className="flex items-center gap-4">
                    <Typography variant="h6" color="blue-gray" className="font-bold">
                        {t('refund_requests_title', { count: list.length })}
                    </Typography>
                </div>
            </div>

            <div className="flex flex-col gap-6 w-full">
                {list.length === 0 ? (
                    <div className="p-12 text-center text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                        {t('no_refund_requests_message')}
                    </div>
                ) : (
                    list.map((item, index) => (
                        <div key={item.id ?? item.refund_id ?? index} className="overflow-hidden rounded-lg shadow-sm">

                            <div className="flex flex-wrap items-center justify-between gap-4">
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <Typography className="font-medium text-gray-600">
                                            {item.refund_id_display || `ID${item.id ?? item.refund_id}`}
                                        </Typography>
                                    </div>
                                    <Typography className="text-gray-600 font-medium">
                                        {t('items_count', { count: item.order?.total_items ?? item.item_count ?? 0 })}
                                    </Typography>
                                    <Typography className="font-medium text-gray-600">
                                        {t('refund_amount_label', { amount: item?.refund_amount && Number(item.refund_amount ?? 0).toLocaleString() || 'N/A' })}
                                    </Typography>
                                    {item?.type === 'managed' && <Typography className="font-medium text-gray-600">
                                        {t('managed_amount_label')}
                                        {item?.type === 'managed' ? ` (${item?.currency_code ?? ''} ${item?.managed_refund_amount})` : 'N/A'}
                                    </Typography>}
                                    <span className={`inline-flex items-center rounded-full px-4 py-1 text-xs font-semibold ${STATUS_STYLES[item.status] ?? 'bg-yellow-100 text-yellow-700'}`}>
                                        {/* {item?.status ? item.status.charAt(0).toUpperCase() + item.status.slice(1).toLowerCase() : ""} */}
                                        {item?.status === 'approved' ? t('approved_status') : item?.status === 'requested' ? t('requested_status') : item?.status === 'rejected' ? t('rejected_status') : item?.status === 'completed' ? t('completed_status') : ''}
                                    </span>
                                    <Typography className="text-gray-500 text-sm">{formatDate(item?.created_at)}</Typography>

                                </div>

                                <div className="flex items-center gap-2">
                                    <IconButton variant="text" size="sm" className="rounded-md bg-gray-100 text-gray-600" onClick={() => handleDownload(item)}>
                                        <ArrowDownTrayIcon className="h-4 w-4" />
                                    </IconButton>

                                    {(user?.roles[0] === 'Finance' || user?.roles[0] === 'Administrator') && item.status === 'requested' && (
                                        <Popover
                                            isOpen={actionPopoverOpen[item.id]}
                                            positions={["bottom", "left"]}
                                            padding={4}
                                            onClickOutside={() => toggleActionPopover(item.id)}
                                            content={
                                                <div className="bg-white shadow-xl rounded-md w-40 flex flex-col z-50 border border-gray-100 py-1">
                                                    <button className="text-left text-sm text-gray-700 hover:bg-gray-100 px-3 py-2" onClick={() => onApproveReject('approved', item.id)}>{t('approve_button')}</button>
                                                    <button className="text-left text-sm text-red-600 hover:bg-gray-100 px-3 py-2" onClick={() => onApproveReject('rejected', item.id)}>{t('reject_button')}</button>
                                                </div>
                                            }
                                        >
                                            <IconButton variant="text" size="sm" className="rounded-md text-gray-600" onClick={() => toggleActionPopover(item.id)}>
                                                <EllipsisVerticalIcon className="h-5 w-5" />
                                            </IconButton>
                                        </Popover>
                                    )}
                                </div>
                            </div>

                            <div className="border-t border-gray-200" />

                            <div className="mt-4">
                                <div className="flex flex-col md:flex-row gap-8">
                                    <Typography className="font-bold text-gray-600 whitespace-nowrap">{t('refund_reason_label')}</Typography>
                                    <Typography className="text-gray-700 text-sm">{item.description ?? item.reason ?? "—"}</Typography>
                                </div>
                            </div>

                            {item.remark && (
                                <div className={`border-t border-gray-200`}>
                                    <div className="flex gap-2 text-sm mt-4 pb-2">
                                        <span className={`font-bold ${item.status === 'rejected' ? 'text-red-700' : 'text-green-700'}`}>
                                            {item.status === 'rejected' ? t('rejected_reason_label') : t('credit_debit_notes_label')}
                                        </span>
                                        <span className="text-gray-700">{item.remark}</span>
                                    </div>
                                </div>
                            )}
                            <div className="border-t border-gray-200" />

                            {item?.managed_adjustments?.length > 0 && (
                                <div className="mt-4 border-t border-gray-100 pt-3">
                                    <h4 className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">
                                        {t('adjustment_history_title')}
                                    </h4>
                                    <ul className="divide-y divide-gray-50 border border-gray-100 rounded-lg bg-gray-50/30">
                                        {item.managed_adjustments.map((row, index) => (
                                            <li
                                                key={row?.order_id || index}
                                                className="flex justify-between items-center p-3 hover:bg-white transition-colors"
                                            >
                                                <div className="flex items-center gap-3">
                                                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-[10px] font-bold text-gray-600">
                                                        {index + 1}
                                                    </span>
                                                    <div>
                                                        <p className="text-xs text-gray-500">{t('order_id_label')}</p>
                                                        <p className="text-sm font-mono font-medium text-gray-700">
                                                            {row?.order_id ?? 'N/A'}
                                                        </p>
                                                    </div>
                                                </div>

                                                <div className="text-right">
                                                    <p className="text-xs text-gray-500">{t('amount_label')}</p>
                                                    <p className="text-sm font-bold text-emerald-600">
                                                        {item?.currency_code ?? ''} {row?.adjusted_amount ?? '0.00'}
                                                    </p>
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>

            {isRefondModalOpen && (
                <RefundApprovalModal
                    isOpen={!!isRefondModalOpen}
                    onClose={() => setIsRefondModalOpen(false)}
                    onConfirm={(data) => approveRefundRequest(data)}
                    loading={loading}
                    payableAmount={currentRefundRequest?.order?.payment_summary?.grand_total}
                    amountPaid={currentRefundRequest?.order?.payment_summary?.paid_amount}
                    userId={targetUserId}
                />
            )}
        </div>
    );
}

export default OrderRefundRequests;